/**
 * Pure, allocation-light mappings from ride physics to audio parameters.
 * Nothing here touches the Web Audio API, so every curve is unit-testable.
 */
import type { CyclingDiscipline, Surface } from '../model';
import type { RideBiome } from '../biomes';

export const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);
export const clampTo = (n: number, lo: number, hi: number) => (n < lo ? lo : n > hi ? hi : n);
export const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
};

/** Wheel, tread and drivetrain geometry per discipline (typical race bikes). */
export interface BikeAcoustics {
  /** Rolling circumference of the tyre in metres. */
  circumference: number;
  /** Tread rows (knob rows) around the tyre; drives the knobby "buzz" pitch. */
  treadRows: number;
  /** 0 = slick, 1 = aggressive knobs. Scales tread buzz and grit. */
  knobby: number;
  /** Freehub engagement points per wheel revolution. */
  engagement: number;
  /** Chainring teeth; chain link pass rate = cadence × teeth. */
  chainring: number;
  /** Metres travelled per crank revolution for a single-speed bike (BMX), else 0 = geared. */
  development: number;
  /** Relative freehub loudness (MTB hubs buzz, road hubs tick). */
  hubLoudness: number;
  /** Resonant centre of a freehub click in Hz. Higher = finer tick. */
  hubTone: number;
  /** Brake squeal fundamental in Hz. */
  squeal: number;
}

export const BIKE_ACOUSTICS: Record<CyclingDiscipline, BikeAcoustics> = {
  road: { circumference: 2.105, treadRows: 96, knobby: .12, engagement: 36, chainring: 50, development: 0, hubLoudness: .55, hubTone: 5600, squeal: 2350 },
  mtb: { circumference: 2.33, treadRows: 58, knobby: 1, engagement: 54, chainring: 32, development: 0, hubLoudness: 1, hubTone: 3500, squeal: 1650 },
  bmx: { circumference: 1.62, treadRows: 46, knobby: .72, engagement: 36, chainring: 44, development: 4.45, hubLoudness: .8, hubTone: 4300, squeal: 2900 },
};

export function wheelRevsPerSecond(speed: number, circumference: number): number {
  return speed > 0 && circumference > 0 ? speed / circumference : 0;
}
export function wheelRpm(speed: number, circumference: number): number {
  return wheelRevsPerSecond(speed, circumference) * 60;
}
/** Freehub pawl clicks per second while coasting: wheel revs × engagement points. */
export function freehubClickRate(speed: number, discipline: CyclingDiscipline): number {
  const bike = BIKE_ACOUSTICS[discipline];
  return wheelRevsPerSecond(speed, bike.circumference) * bike.engagement;
}
/** Knob-row impacts per second: the pitch of the knobby-tyre hum on hard ground. */
export function treadPassRate(speed: number, discipline: CyclingDiscipline): number {
  const bike = BIKE_ACOUSTICS[discipline];
  return wheelRevsPerSecond(speed, bike.circumference) * bike.treadRows;
}
/** Rider cadence in rpm. Geared bikes shift to stay near a comfortable band; BMX is single-speed. */
export function cadenceRpm(speed: number, discipline: CyclingDiscipline, effort = .45): number {
  const bike = BIKE_ACOUSTICS[discipline];
  if (bike.development > 0) return clampTo(speed / bike.development * 60, 30, 175);
  const base = discipline === 'road' ? 64 + speed * 2.4 : 58 + speed * 2.2;
  return clampTo(base + effort * 10, 50, discipline === 'road' ? 112 : 104);
}
/** Chain link pass frequency in Hz (links meshing with the chainring). */
export function chainLinkRate(cadence: number, discipline: CyclingDiscipline): number {
  return Math.max(0, cadence) / 60 * BIKE_ACOUSTICS[discipline].chainring;
}

export type TyreLayer = 'hiss' | 'spray' | 'crunch' | 'grit' | 'rumble' | 'thump' | 'tread';
export type TyreGains = Record<TyreLayer, number>;

/** How much of each rolling texture a surface produces at full speed. */
export const SURFACE_WEIGHTS: Record<Surface, TyreGains> = {
  tarmac: { hiss: 1, spray: 0, crunch: 0, grit: .05, rumble: .3, thump: 0, tread: 1 },
  wet: { hiss: .85, spray: 1, crunch: 0, grit: 0, rumble: .22, thump: 0, tread: .6 },
  gravel: { hiss: .18, spray: 0, crunch: 1, grit: .45, rumble: .55, thump: .1, tread: .3 },
  hardpack: { hiss: .3, spray: 0, crunch: .14, grit: .65, rumble: 1, thump: .16, tread: .78 },
  loam: { hiss: .06, spray: 0, crunch: .04, grit: .14, rumble: .55, thump: 1, tread: .22 },
};
/** Speed exponents: hiss rises steeply, grains are audible even at a crawl. */
const SPEED_EXPONENT: TyreGains = { hiss: 1.55, spray: 1.25, crunch: .8, grit: 1, rumble: 1.05, thump: .9, tread: 1.3 };
const LAYERS = Object.keys(SPEED_EXPONENT) as TyreLayer[];

/**
 * Rolling layer gains (0..~1.3) for a surface, speed and tyre. Knobby tyres
 * buzz and grind more; slick road tyres hiss cleanly.
 */
export function surfaceLayerGains(surface: Surface, speed: number, discipline: CyclingDiscipline, out: TyreGains = { hiss: 0, spray: 0, crunch: 0, grit: 0, rumble: 0, thump: 0, tread: 0 }): TyreGains {
  const weights = SURFACE_WEIGHTS[surface] ?? SURFACE_WEIGHTS.hardpack;
  const v = clampTo(speed / 18, 0, 1.6);
  const fade = smoothstep(.15, 1.2, speed);
  const knob = BIKE_ACOUSTICS[discipline].knobby;
  for (const layer of LAYERS) {
    let gain = weights[layer] * Math.pow(v, SPEED_EXPONENT[layer]) * fade;
    if (layer === 'tread') gain *= .15 + .85 * knob;
    else if (layer === 'grit' || layer === 'crunch') gain *= .8 + .25 * knob;
    else if (layer === 'hiss') gain *= 1.05 - .25 * knob;
    out[layer] = Math.min(1.5, gain);
  }
  return out;
}
/** Lowpass cutoff for tyre hiss: brighter as speed rises. */
export function hissCutoff(speed: number): number {
  return 2200 + 6800 * clamp01(speed / 22);
}

/** Wind level follows dynamic pressure (speed²); brightness rises with it. */
export function windProfile(speed: number, airborne: boolean) {
  const q = clampTo((speed / 20) ** 2, 0, 1.6);
  return {
    level: q * (airborne ? .5 : 1),
    centre: 320 + 1500 * clamp01(speed / 24) ** 1.5,
    buffet: clampTo(((speed - 8) / 16) ** 2 * Math.sign(speed - 8), 0, 1.2),
  };
}

/** Ambience ducks as the rider gets fast and the wind takes over. */
export function ambienceDuck(speed: number): number {
  return 1 - .55 * smoothstep(6, 20, speed);
}

/** Brake intensity from longitudinal deceleration (m/s²); ignores impact spikes. */
export function brakeIntensity(deceleration: number, speed: number): number {
  if (!(deceleration > 3) || deceleration > 45 || speed < .35) return 0;
  return clamp01((deceleration - 3) / 14) * smoothstep(.35, 2.5, speed);
}
/** Tyre scrub from lateral slip and drift. */
export function skidIntensity(slip: number, drifting: boolean, speed: number): number {
  return clamp01(clamp01((slip - .12) / .8) + (drifting ? .22 : 0)) * smoothstep(1, 6, speed);
}
/** Skids are loudest on tarmac and turn into scatter on loose ground. */
export const SKID_SURFACE: Record<Surface, { scrub: number; squeal: number; scatter: number }> = {
  tarmac: { scrub: 1, squeal: 1, scatter: 0 },
  wet: { scrub: .7, squeal: .3, scatter: 0 },
  hardpack: { scrub: .6, squeal: .08, scatter: .35 },
  gravel: { scrub: .35, squeal: 0, scatter: 1 },
  loam: { scrub: .28, squeal: 0, scatter: .25 },
};

/** Landing loudness from vertical touchdown speed (m/s). */
export function landingIntensity(verticalSpeed: number): number {
  return clampTo(Math.abs(verticalSpeed) / 8, .18, 1.25);
}

/** Breathing rate (breaths per second) and level from heart rate, effort and fatigue. */
export function breathProfile(heartRate: number, effort: number, stamina: number) {
  const hr = clampTo(heartRate, 60, 205);
  const rate = clampTo(.24 + (hr - 90) / 100 * .5, .22, .82);
  const exertion = clamp01((hr - 104) / 78) ** 1.3;
  const level = clamp01(exertion * (.55 + .45 * clamp01(effort)) + (1 - clamp01(stamina / 100)) * .25);
  return { rate, level };
}

export interface SpatialRival { id: number; distance: number; lateral: number; finished?: boolean }
/**
 * Listener-relative rival position for a PannerNode. The listener faces -Z,
 * +X is the rider's right. Gain fades the voice out towards `range` metres.
 */
export function rivalSpatial(playerDistance: number, playerLateral: number, rival: SpatialRival, range = 40) {
  const ahead = rival.distance - playerDistance;
  const x = rival.lateral - playerLateral;
  const z = -ahead;
  const d = Math.hypot(ahead, x);
  const gain = rival.finished ? 0 : 1 - smoothstep(range * .6, range, d);
  return { x, y: 0, z, distance: d, gain };
}
/** Nearest rivals within range, closest first, at most `voices`. */
export function nearestRivals<T extends SpatialRival>(rivals: readonly T[], playerDistance: number, playerLateral: number, voices = 4, range = 40): T[] {
  return rivals
    .filter(r => !r.finished && Math.hypot(r.distance - playerDistance, r.lateral - playerLateral) < range)
    .sort((a, b) => Math.hypot(a.distance - playerDistance, a.lateral - playerLateral) - Math.hypot(b.distance - playerDistance, b.lateral - playerLateral))
    .slice(0, voices);
}
/** Equal-power pan (-1..1) used when PannerNode is unavailable. */
export function equalPowerPan(x: number, z: number): { left: number; right: number; pan: number } {
  const pan = clampTo(x / Math.max(1.5, Math.hypot(x, z)), -1, 1);
  const angle = (pan + 1) * Math.PI / 4;
  return { left: Math.cos(angle), right: Math.sin(angle), pan };
}

/** A pentatonic ladder for the gate chime: streaks climb, capped at 8 steps. */
const LADDER = [0, 2, 4, 7, 9, 12, 14, 16, 19];
export function gateChimeNotes(streak: number): [number, number] {
  const step = LADDER[clampTo(Math.floor(streak) - 1, 0, LADDER.length - 1)];
  const root = 1046.5 * 2 ** (step / 12); // C6 upward
  return [root, root * 2 ** (7 / 12)]; // a bright rising fifth
}

export interface ReverbProfile {
  /** Tail length in seconds. */
  length: number;
  /** RT60 of the diffuse tail. */
  rt60: number;
  /** Discrete reflections: [delay seconds, gain]. */
  echoes: [number, number][];
  /** 0 = bright, 1 = very dark tail. */
  damping: number;
  /** Diffuse density 0..1 (forest = dense, open = sparse). */
  density: number;
  /** Send levels per bus. */
  sends: { ride: number; fx: number; ui: number; ambience: number; rivals: number };
}
export const REVERB_PROFILES: Record<RideBiome, ReverbProfile> = {
  alpine: { length: 1.6, rt60: 1.35, echoes: [[.045, .35], [.083, .25], [.13, .18]], damping: .55, density: 1, sends: { ride: .05, fx: .22, ui: .16, ambience: .32, rivals: .2 } },
  canyon: { length: 2.8, rt60: 2.3, echoes: [[.19, .62], [.34, .42], [.52, .3], [.71, .18], [.93, .11]], damping: .35, density: .55, sends: { ride: .09, fx: .3, ui: .2, ambience: .38, rivals: .26 } },
  coast: { length: .75, rt60: .5, echoes: [[.028, .2]], damping: .4, density: .35, sends: { ride: .03, fx: .12, ui: .12, ambience: .14, rivals: .1 } },
  meadow: { length: .7, rt60: .45, echoes: [[.035, .15]], damping: .5, density: .3, sends: { ride: .03, fx: .12, ui: .12, ambience: .16, rivals: .1 } },
  harbour: { length: 1.3, rt60: .95, echoes: [[.11, .38], [.24, .2]], damping: .45, density: .6, sends: { ride: .05, fx: .18, ui: .14, ambience: .24, rivals: .16 } },
  park: { length: 1.2, rt60: .85, echoes: [[.07, .3], [.16, .2]], damping: .45, density: .65, sends: { ride: .05, fx: .18, ui: .14, ambience: .26, rivals: .16 } },
};
